import React, { useState } from 'react';
import { FilterIcon, EyeIcon, DownloadIcon, SearchIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import DateRangePicker from '../../components/ui/DateRangePicker';
import ExportMenu from '../../components/ui/ExportMenu';
import Modal from '../../components/ui/Modal';
// Mock audit log data
const auditLogsData = [{
  id: '1',
  timestamp: '2023-07-15T10:30:00Z',
  userName: 'John Smith',
  userId: 'U1023',
  role: 'Admin',
  action: 'Created Merchant',
  module: 'Merchant Profile',
  objectId: 'M2034',
  ipAddress: '192.168.1.1',
  status: 'success',
  remarks: 'New merchant onboarding',
  details: {
    before: null,
    after: {
      id: 'M2034',
      name: 'Coffee Shop Inc',
      status: 'active',
      createdAt: '2023-07-15T10:30:00Z',
      createdBy: 'U1023'
    }
  }
}, {
  id: '2',
  timestamp: '2023-07-15T11:45:00Z',
  userName: 'Jane Doe',
  userId: 'U1024',
  role: 'Manager',
  action: 'Updated Limits',
  module: 'Merchant Settings',
  objectId: 'M2034',
  ipAddress: '192.168.1.2',
  status: 'success',
  remarks: 'Increased transaction limit',
  details: {
    before: {
      dailyLimit: 5000,
      monthlyLimit: 100000
    },
    after: {
      dailyLimit: 10000,
      monthlyLimit: 200000
    }
  }
}, {
  id: '3',
  timestamp: '2023-07-15T12:15:00Z',
  userName: 'Robert Johnson',
  userId: 'U1025',
  role: 'Agent',
  action: 'Login Attempt',
  module: 'Authentication',
  objectId: 'U1025',
  ipAddress: '192.168.1.3',
  status: 'failed',
  remarks: 'Invalid credentials',
  details: {
    reason: 'Invalid password',
    attempts: 2
  }
}, {
  id: '4',
  timestamp: '2023-07-15T13:30:00Z',
  userName: 'Sarah Williams',
  userId: 'U1026',
  role: 'Auditor',
  action: 'Exported Report',
  module: 'Reports',
  objectId: 'R1001',
  ipAddress: '192.168.1.4',
  status: 'success',
  remarks: 'Monthly transaction report',
  details: {
    reportType: 'Monthly Transactions',
    format: 'CSV',
    filters: {
      dateRange: 'July 2023',
      merchants: 'All'
    }
  }
}, {
  id: '5',
  timestamp: '2023-07-15T14:45:00Z',
  userName: 'Michael Brown',
  userId: 'U1027',
  role: 'Admin',
  action: 'Deleted User',
  module: 'User Management',
  objectId: 'U1028',
  ipAddress: '192.168.1.5',
  status: 'success',
  remarks: 'User no longer with company',
  details: {
    before: {
      id: 'U1028',
      name: 'James Wilson',
      email: 'james@example.com',
      role: 'Agent',
      status: 'active'
    },
    after: null
  }
}, {
  id: '6',
  timestamp: '2023-07-15T15:00:00Z',
  userName: 'Emily Davis',
  userId: 'U1029',
  role: 'Manager',
  action: 'Generated QR Code',
  module: 'QRC Generator',
  objectId: 'Q5001',
  ipAddress: '192.168.1.6',
  status: 'success',
  remarks: 'For merchant point of sale',
  details: {
    merchantId: 'M2035',
    qrType: 'Static',
    amount: null
  }
}, {
  id: '7',
  timestamp: '2023-07-15T16:15:00Z',
  userName: 'David Wilson',
  userId: 'U1030',
  role: 'Checker',
  action: 'Approved Merchant',
  module: 'Merchant Approval',
  objectId: 'M2036',
  ipAddress: '192.168.1.7',
  status: 'success',
  remarks: 'All documents verified',
  details: {
    before: {
      status: 'pending'
    },
    after: {
      status: 'approved'
    }
  }
}, {
  id: '8',
  timestamp: '2023-07-15T17:30:00Z',
  userName: 'Linda Miller',
  userId: 'U1031',
  role: 'Maker',
  action: 'Created Role',
  module: 'Role Management',
  objectId: 'R101',
  ipAddress: '192.168.1.8',
  status: 'success',
  remarks: 'New support role',
  details: {
    roleName: 'Support Agent',
    permissions: ['view_merchants', 'view_transactions', 'create_tickets']
  }
}, {
  id: '9',
  timestamp: '2023-07-15T18:45:00Z',
  userName: 'James Taylor',
  userId: 'U1032',
  role: 'Admin',
  action: 'System Configuration',
  module: 'Settings',
  objectId: 'S001',
  ipAddress: '192.168.1.9',
  status: 'failed',
  remarks: 'Invalid configuration',
  details: {
    error: 'Invalid timeout value',
    submitted: {
      sessionTimeout: -1
    }
  }
}, {
  id: '10',
  timestamp: '2023-07-15T19:00:00Z',
  userName: 'Patricia Anderson',
  userId: 'U1033',
  role: 'Checker',
  action: 'Rejected Transaction',
  module: 'Transaction Approval',
  objectId: 'T10001',
  ipAddress: '192.168.1.10',
  status: 'success',
  remarks: 'Suspicious activity',
  details: {
    transactionAmount: 50000,
    merchant: 'M2037',
    reason: 'Amount exceeds usual pattern'
  }
}];
interface AuditLog {
  id: string;
  timestamp: string;
  userName: string;
  userId: string;
  role: string;
  action: string;
  module: string;
  objectId: string;
  ipAddress: string;
  status: string;
  remarks: string;
  details: any;
}
const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>(auditLogsData);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>(auditLogsData);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  // Filter states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [userFilter, setUserFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };
  const handleExport = (format: 'csv' | 'xlsx' | 'pdf') => {
    console.log(`Exporting audit logs as ${format}`);
    // Implement export logic here
  };
  const applyFilters = () => {
    let filtered = [...logs];
    if (startDate) {
      filtered = filtered.filter(log => new Date(log.timestamp) >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(log => new Date(log.timestamp) <= endDate);
    }
    if (userFilter) {
      filtered = filtered.filter(log => log.userName.toLowerCase().includes(userFilter.toLowerCase()) || log.userId.toLowerCase().includes(userFilter.toLowerCase()));
    }
    if (roleFilter) {
      filtered = filtered.filter(log => log.role === roleFilter);
    }
    if (moduleFilter) {
      filtered = filtered.filter(log => log.module === moduleFilter);
    }
    if (actionFilter) {
      filtered = filtered.filter(log => log.action.toLowerCase().includes(actionFilter.toLowerCase()));
    }
    if (ipFilter) {
      filtered = filtered.filter(log => log.ipAddress.includes(ipFilter));
    }
    if (showFailedOnly) {
      filtered = filtered.filter(log => log.status === 'failed');
    }
    setFilteredLogs(filtered);
  };
  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setUserFilter('');
    setRoleFilter('');
    setModuleFilter('');
    setActionFilter('');
    setIpFilter('');
    setShowFailedOnly(false);
    setFilteredLogs(logs);
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <StatusBadge status="success" text="Success" data-id="element-7630" />;
      case 'failed':
        return <StatusBadge status="danger" text="Failed" data-id="element-7631" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-7632" />;
    }
  };
  const getActionBadge = (action: string) => {
    let status: 'success' | 'warning' | 'danger' | 'info' | 'default' = 'default';
    if (action.includes('Create') || action.includes('Add') || action.includes('Generate')) {
      status = 'success';
    } else if (action.includes('Update') || action.includes('Edit') || action.includes('Modify')) {
      status = 'info';
    } else if (action.includes('Delete') || action.includes('Remove')) {
      status = 'danger';
    } else if (action.includes('Approve') || action.includes('Reject') || action.includes('Export')) {
      status = 'warning';
    }
    return <StatusBadge status={status} text={action} data-id="element-7633" />;
  };
  // Get unique values for filters
  const uniqueRoles = Array.from(new Set(logs.map(log => log.role)));
  const uniqueModules = Array.from(new Set(logs.map(log => log.module)));
  const columns = [{
    header: 'Timestamp',
    accessor: (log: AuditLog) => formatDateTime(log.timestamp),
    sortable: true
  }, {
    header: 'User',
    accessor: (log: AuditLog) => <div data-id="element-7634">
          <div className="font-medium" data-id="element-7635">{log.userName}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-7636">
            {log.userId}
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Role',
    accessor: 'role',
    sortable: true
  }, {
    header: 'Action',
    accessor: (log: AuditLog) => getActionBadge(log.action),
    sortable: false
  }, {
    header: 'Module',
    accessor: 'module',
    sortable: true
  }, {
    header: 'Object ID',
    accessor: 'objectId',
    sortable: true
  }, {
    header: 'IP Address',
    accessor: 'ipAddress',
    sortable: true
  }, {
    header: 'Status',
    accessor: (log: AuditLog) => getStatusBadge(log.status),
    sortable: false
  }];
  return <div className="space-y-6" data-id="element-7637">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-7638">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-7639">
          Audit Logs
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-7640">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7641">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-7642" />
            Filters
          </button>
          <button onClick={() => {
          setLogs(auditLogsData);
          setFilteredLogs(auditLogsData);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7643">
            <div className="h-4 w-4 mr-2" data-id="element-7644" />
            Refresh
          </button>
          <ExportMenu onExport={handleExport} data-id="element-7645" />
        </div>
      </div>

      {showFilters && <Card data-id="element-7646">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7647">
            <div className="md:col-span-3" data-id="element-7648">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7649">
                Date Range
              </label>
              <DateRangePicker startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} data-id="element-7650" />
            </div>
            <div data-id="element-7651">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7652">
                User / User ID
              </label>
              <div className="relative" data-id="element-7653">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-7654">
                  <SearchIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" data-id="element-7655" />
                </div>
                <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full pl-10 p-2.5" placeholder="Search user or ID" value={userFilter} onChange={e => setUserFilter(e.target.value)} data-id="element-7656" />
              </div>
            </div>
            <div data-id="element-7657">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7658">
                Role
              </label>
              <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} data-id="element-7659">
                <option value="" data-id="element-7660">All Roles</option>
                {uniqueRoles.map(role => <option key={role} value={role} data-id="element-7661">
                    {role}
                  </option>)}
              </select>
            </div>
            <div data-id="element-7662">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7663">
                Module
              </label>
              <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} data-id="element-7664">
                <option value="" data-id="element-7665">All Modules</option>
                {uniqueModules.map(module => <option key={module} value={module} data-id="element-7666">
                    {module}
                  </option>)}
              </select>
            </div>
            <div data-id="element-7667">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7668">
                Action Type
              </label>
              <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" placeholder="e.g. Create, Update, Delete" value={actionFilter} onChange={e => setActionFilter(e.target.value)} data-id="element-7669" />
            </div>
            <div data-id="element-7670">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7671">
                IP Address
              </label>
              <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" placeholder="e.g. 192.168.1.1" value={ipFilter} onChange={e => setIpFilter(e.target.value)} data-id="element-7672" />
            </div>
          </div>
          <div className="mt-4 flex items-center" data-id="element-7673">
            <input id="failed-only" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={showFailedOnly} onChange={e => setShowFailedOnly(e.target.checked)} data-id="element-7674" />
            <label htmlFor="failed-only" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-7675">
              Show Only Failed Actions
            </label>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-7676">
            <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7677">
              Reset
            </button>
            <button onClick={applyFilters} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-7678">
              Apply Filters
            </button>
          </div>
        </Card>}

      <Card data-id="element-7679">
        <Table columns={columns} data={filteredLogs} keyField="id" actions={log => <div className="flex justify-end" data-id="element-7681">
              <button onClick={() => handleViewDetails(log)} className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="View Details" data-id="element-7682">
                <EyeIcon className="h-5 w-5" data-id="element-7683" />
              </button>
            </div>} data-id="element-7680" />
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} title="Audit Log Details" size="lg" data-id="element-7684">
        {selectedLog && <div className="space-y-4" data-id="element-7685">
            <div className="flex items-center space-x-3" data-id="element-7686">
              {selectedLog.status === 'success' ? <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-7687" /> : <XCircleIcon className="h-6 w-6 text-red-500" data-id="element-7688" />}
              <div data-id="element-7689">
                <h3 className="text-lg font-medium" data-id="element-7690">{selectedLog.action}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7691">
                  {formatDateTime(selectedLog.timestamp)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-7692">
              <div data-id="element-7693">
                <h4 className="font-medium mb-2" data-id="element-7694">User Information</h4>
                <div className="space-y-2 text-sm" data-id="element-7695">
                  <p data-id="element-7696">
                    <span className="font-medium" data-id="element-7697">Name:</span>{' '}
                    {selectedLog.userName}
                  </p>
                  <p data-id="element-7698">
                    <span className="font-medium" data-id="element-7699">ID:</span>{' '}
                    {selectedLog.userId}
                  </p>
                  <p data-id="element-7700">
                    <span className="font-medium" data-id="element-7701">Role:</span>{' '}
                    {selectedLog.role}
                  </p>
                  <p data-id="element-7702">
                    <span className="font-medium" data-id="element-7703">IP Address:</span>{' '}
                    {selectedLog.ipAddress}
                  </p>
                </div>
              </div>
              <div data-id="element-7704">
                <h4 className="font-medium mb-2" data-id="element-7705">Action Information</h4>
                <div className="space-y-2 text-sm" data-id="element-7706">
                  <p data-id="element-7707">
                    <span className="font-medium" data-id="element-7708">Module:</span>{' '}
                    {selectedLog.module}
                  </p>
                  <p data-id="element-7709">
                    <span className="font-medium" data-id="element-7710">Object ID:</span>{' '}
                    {selectedLog.objectId}
                  </p>
                  <p data-id="element-7711">
                    <span className="font-medium" data-id="element-7712">Status:</span>{' '}
                    {selectedLog.status}
                  </p>
                  <p data-id="element-7713">
                    <span className="font-medium" data-id="element-7714">Remarks:</span>{' '}
                    {selectedLog.remarks}
                  </p>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-7715">
              <h4 className="font-medium mb-2" data-id="element-7716">Details</h4>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md overflow-x-auto" data-id="element-7717">
                <pre className="text-xs text-gray-800 dark:text-gray-200" data-id="element-7718">
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default AuditLogs;